import * as React from 'react';
import type {CSSProperties, ComponentProps, ReactNode} from 'react';
import {ResponsiveContainer, Tooltip as RechartsTooltip} from 'recharts';
import type {TooltipContentProps} from 'recharts';
import type {
  NameType,
  Payload,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';

import {cn} from '../lib/utils';

export type ChartConfig = Record<string, {label: ReactNode; color: string}>;

const ChartContext = React.createContext<ChartConfig | null>(null);

function useChartConfig(): ChartConfig {
  const config = React.useContext(ChartContext);
  if (!config) {
    throw new Error('useChartConfig must be used within a <ChartContainer />');
  }
  return config;
}

interface ChartContainerProps extends ComponentProps<'div'> {
  config: ChartConfig;
  children: ComponentProps<typeof ResponsiveContainer>['children'];
}

const ChartContainer = React.forwardRef<HTMLDivElement, ChartContainerProps>(
  ({config, children, className, style, ...props}, ref) => {
    const colorVars = React.useMemo(
      () =>
        Object.fromEntries(
          Object.entries(config).map(([key, item]) => [
            `--color-${key}`,
            item.color,
          ]),
        ) as CSSProperties,
      [config],
    );

    return (
      <ChartContext.Provider value={config}>
        <div
          ref={ref}
          data-slot="chart"
          className={cn(
            'flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line]:stroke-border/50 [&_.recharts-surface]:outline-hidden',
            className,
          )}
          style={{...colorVars, ...style}}
          {...props}
        >
          <ResponsiveContainer>{children}</ResponsiveContainer>
        </div>
      </ChartContext.Provider>
    );
  },
);
ChartContainer.displayName = 'ChartContainer';

const ChartTooltip = RechartsTooltip;

type ChartTooltipContentProps = Partial<
  Pick<
    TooltipContentProps<ValueType, NameType>,
    'active' | 'payload' | 'label' | 'accessibilityLayer'
  >
> & {
  className?: string;
  hideLabel?: boolean;
  hideItems?: boolean;
  labelFormatter?: (
    label: ReactNode,
    payload: ReadonlyArray<Payload<ValueType, NameType>>,
  ) => ReactNode;
  valueFormatter?: (
    value: ValueType | undefined,
    item: Payload<ValueType, NameType>,
  ) => ReactNode;
};

function ChartTooltipContent({
  active,
  payload,
  label,
  accessibilityLayer = false,
  className,
  hideLabel = false,
  hideItems = false,
  labelFormatter,
  valueFormatter,
}: ChartTooltipContentProps) {
  const config = useChartConfig();

  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div
      {...(accessibilityLayer && {role: 'status', 'aria-live': 'assertive'})}
      className={cn(
        'grid min-w-32 gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground shadow-lg',
        className,
      )}
    >
      {!hideLabel && (
        <div className="font-medium">
          {labelFormatter ? labelFormatter(label, payload) : label}
        </div>
      )}
      {!hideItems && (
        <div className="grid gap-1">
          {payload.map((item) => {
            const key = String(item.dataKey ?? item.name ?? '');
            const itemConfig = config[key];
            return (
              <div key={key} className="flex items-center gap-2">
                <span
                  className="size-2 shrink-0 rounded-full"
                  style={{backgroundColor: itemConfig?.color ?? item.color}}
                />
                <span className="text-primary-foreground/70">
                  {itemConfig?.label ?? item.name}
                </span>
                <span className="ml-auto font-semibold tabular-nums">
                  {valueFormatter
                    ? valueFormatter(item.value, item)
                    : item.value?.toLocaleString()}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export {ChartContainer, ChartTooltip, ChartTooltipContent, useChartConfig};
